import React, { memo } from 'react';
import {
  Text,
  Group,
  Tooltip,
  RingProgress,
  useComputedColorScheme,
  ActionIcon,
} from '@mantine/core';
import { IconChevronRight, IconInfoCircle, IconArrowUpRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Grade, UserCompetency } from '../../types';
import { useTranslation } from 'react-i18next';

interface GradeHeaderProps {
  grade: Grade;
  gradeIdx: number;
  isCollapsed: boolean;
  isAnimating: boolean;
  userCompetencies: UserCompetency[];
  currentGrade?: Grade;
  nextGrade?: Grade;
  onToggleCollapse: (gradeIdx: number) => void;
  competencyMapId?: string;
  hideProgress?: boolean;
}

const GradeHeaderComponent: React.FC<GradeHeaderProps> = ({
  grade,
  gradeIdx,
  isCollapsed,
  isAnimating,
  userCompetencies,
  currentGrade,
  nextGrade,
  onToggleCollapse,
  competencyMapId,
  hideProgress = false,
}) => {
  const navigate = useNavigate();
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';
  const { t } = useTranslation();

  // Функция для вычисления прогресса по грейду
  const getGradeProgress = (grade: Grade): number => {
    const totalCompetencies = grade.competencies.length;
    const acquiredCompetencies = grade.competencies.filter((comp) =>
      userCompetencies.some((userComp) => userComp.competencyId === comp._id),
    ).length;

    return totalCompetencies > 0 ? Math.round((acquiredCompetencies / totalCompetencies) * 100) : 0;
  };

  const shortText = (() => {
    const words = grade.title.split(' ');
    return words.length > 1 ? `${words[0][0]}(${words[1]})` : words[0][0];
  })();

  const iconColor = isDark ? 'var(--mantine-color-gray-3)' : '#868e96';
  const rootColor = isDark ? 'var(--mantine-color-dark-4)' : '#f1f3f4';
  const textColor = isDark ? 'var(--mantine-color-gray-2)' : undefined;

  return (
    <Group
      justify="center"
      wrap="nowrap"
      className={`grade-header-cell ${gradeIdx ? '' : 'grade-header-cell__first'}`}
      gap="xs"
      style={{
        color: textColor,
      }}
      onClick={() => onToggleCollapse(gradeIdx)}
    >
      {grade._id === currentGrade?._id && (
        <div className="grade-header-cell__current-grade-indicator" />
      )}
      {grade._id === nextGrade?._id && <div className="grade-header-cell__next-grade-indicator" />}

      <IconChevronRight
        size={14}
        color={iconColor}
        style={{
          flexShrink: 0,
          transform: isCollapsed ? 'rotate(0deg)' : 'rotate(-180deg)',
          transition: 'transform 0.3s ease-in-out',
        }}
      />
      <Text
        fw={600}
        style={{
          fontSize: 'inherit',
          transition: 'all 0.3s ease-in-out',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: textColor,
        }}
      >
        <span
          className={`grade-title ${currentGrade && grade._id === currentGrade._id ? 'grade-title--current' : ''} ${nextGrade && grade._id === nextGrade._id ? 'grade-title--next' : ''}`}
        >
          {isCollapsed || isAnimating ? shortText : grade.title}
        </span>
      </Text>
      <Group
        gap={2}
        wrap="nowrap"
        style={{ display: isCollapsed || isAnimating ? 'none' : 'flex' }}
      >
        {!hideProgress && (
          <RingProgress
            size={18}
            thickness={2}
            sections={[
              {
                value: getGradeProgress(grade),
                color: getGradeProgress(grade) === 100 ? 'teal' : 'teal.5',
              },
            ]}
            rootColor={rootColor}
            style={{ flexShrink: 0 }}
          />
        )}
        {grade.description && (
          <Tooltip
            style={{ maxWidth: 300 }}
            label={
              <div style={{ paddingBottom: 8, paddingTop: 4 }}>
                <div style={{ marginBottom: 4 }}>{grade.description}</div>
                {grade.qualification && (
                  <div style={{ fontSize: 12 }}>
                    <span className="dimmed">{t('gradeHeader.qualification')}:</span>{' '}
                    {grade.qualification}
                  </div>
                )}
                {grade.position && (
                  <div style={{ fontSize: 12 }}>
                    <span className="dimmed">{t('gradeHeader.position')}:</span> {grade.position}
                  </div>
                )}
              </div>
            }
            position="bottom"
            withArrow
            multiline
          >
            <IconInfoCircle size={14} color={iconColor} style={{ flexShrink: 0, cursor: 'help' }} />
          </Tooltip>
        )}
        <Tooltip label={t('gradeHeader.more')} position="bottom" withArrow>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            style={{ flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/competency-maps/${competencyMapId}/${grade._id}`);
            }}
          >
            <IconArrowUpRight size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
};

export const GradeHeader = memo(GradeHeaderComponent);
